/**
 * Purchase method validator and utilities.
 *
 * Purchase method indicates how the buyer acquires the product:
 * - 1: En consignación (C)
 * - 2: En propiedad (P)
 *
 * Only values 1 and 2 are valid.
 */

import { PURCHASE_METHOD_CONST } from './purchase-method.dict';
import { PurchaseMethodResponseStatus, PURCHASE_METHOD_RESPONSE } from '../integration-ibm';

/**
 * Validates if the provided purchaseMethod is valid.
 *
 * @param purchaseMethod - The purchase method value to validate (1 or 2).
 * @returns true if purchaseMethod is 1 or 2, false otherwise.
 * @throws Does not throw; returns false for invalid values.
 */
export function isValidPurchaseMethod(purchaseMethod?: number): boolean {
    return purchaseMethod === 1 || purchaseMethod === 2;
}

export function getPurchaseMethodIBM(purchaseMethod: number): string {
  if (purchaseMethod === 1) return 'C';
  if (purchaseMethod === 2) return 'P';
  return '';
}

/**
 * Inputs needed to resolve the main Order.purchaseMethod field's value,
 * editability, and visibility for a single order.
 *
 * Does NOT cover Canje/Contrapartida: that default (PURCHASE_METHOD_CONST.PROPIEDAD)
 * only ever applies to the separate Exchange.purchaseMethod field, forced in
 * src/app/shared/order-exchange.ts (getExchangeInitial, getProformsOfExchange) and
 * in the dedicated exchange components (add-exchange, exchange-details) — never on
 * this one. This type intentionally has no canje-related field so callers cannot
 * accidentally wire Canje state into the wrong field.
 */
export interface PurchaseMethodResolutionInput {
  /** businessParticularities.isFasonContract for this order. */
  isFasonContract: boolean;
  /** The selected buyer's OnlySell value (array or number already reduced to a single number by the caller; 0/absent means no OnlySell restriction). */
  onlySell: number;
  /** Whether an IBM per-buyer default (COMODEF) was resolved for the current buyer. */
  hasBuyerDefault: boolean;
  /** The IBM per-buyer default value (1 or 2), only meaningful when hasBuyerDefault is true. */
  buyerDefaultValue?: number;
}

export interface PurchaseMethodResolution {
  /** Resolved purchaseMethod value; 0 means "no value" (hidden or not yet selected). */
  value: number;
  /** Whether the operator may change the resolved value. */
  isEditable: boolean;
  /** Whether the field should be rendered at all. */
  isVisible: boolean;
}

/**
 * Resolves value/editability/visibility for the main Order.purchaseMethod field,
 * applying priority: Contrato Fasón > IBM per-buyer default (COMODEF) > manual entry.
 *
 * - Fasón always wins: fixed at "En propiedad", non-editable, and visible even when
 *   OnlySell is 0 (R1).
 * - Otherwise, visibility still follows the existing OnlySell > 0 rule
 *   (Docs/01-Contexto-Negocio/PurchaseMethod.md:16-21) — unchanged.
 * - When visible and not Fasón, a valid buyer default pre-fills the field but keeps
 *   it editable (R3); no default (or an invalid one) leaves it empty and editable.
 */
export function resolvePurchaseMethodDefault(
  input: PurchaseMethodResolutionInput
): PurchaseMethodResolution {
  if (input.isFasonContract) {
    return {
      value: PURCHASE_METHOD_CONST['PROPIEDAD'],
      isEditable: false,
      isVisible: true,
    };
  }

  const isVisible = input.onlySell > 0;
  if (!isVisible) {
    return { value: 0, isEditable: false, isVisible: false };
  }

  if (input.hasBuyerDefault && isValidPurchaseMethod(input.buyerDefaultValue)) {
    return { value: input.buyerDefaultValue as number, isEditable: true, isVisible: true };
  }

  return { value: 0, isEditable: true, isVisible: true };
}

export interface BuyerPurchaseMethodDefault {
  /** Whether IBM (COMODEF) has a parametrized default for this buyer. */
  hasDefault: boolean;
  /** The default value (1 or 2), only meaningful when hasDefault is true. */
  value: number;
}

/**
 * Maps COMODEF's raw "Valor por Defecto" ('Sin Valor' | 'C' | 'P') into a
 * { hasDefault, value } pair ready for resolvePurchaseMethodDefault's
 * hasBuyerDefault/buyerDefaultValue inputs.
 *
 * @param valorPorDefecto - The raw 'Valor por Defecto' string from IBM's
 * PurchaseMethodDefault response (shared-logic/integration-ibm).
 */
export function mapBuyerPurchaseMethodDefault(
  valorPorDefecto: string
): BuyerPurchaseMethodDefault {
  if (valorPorDefecto === 'C') {
    return { hasDefault: true, value: PURCHASE_METHOD_CONST['CONSIGNACION'] };
  }
  if (valorPorDefecto === 'P') {
    return { hasDefault: true, value: PURCHASE_METHOD_CONST['PROPIEDAD'] };
  }
  return { hasDefault: false, value: 0 };
}

/* ---------------- Gate del boton por combinacion ---------------- */

/* The buyer and purchase-method combination a verdict belongs to. Carrying it with the
verdict is what stops a result obtained for one combination from authorising another. */
export interface PurchaseMethodPair {
  buyerCode: number;
  purchaseMethod: number;
}

/* A check that is still running. It is not a response status, so it lives here rather
than beside the classified answers: the integration never returns it. */
export const PURCHASE_METHOD_PENDING = 'pending';

export type PurchaseMethodGateStatus =
  PurchaseMethodResponseStatus | typeof PURCHASE_METHOD_PENDING;

export interface PurchaseMethodVerdict {
  /* null while nothing has been checked, so it can never match a live combination. */
  pair: PurchaseMethodPair | null;
  status: PurchaseMethodGateStatus;
}

export function isSamePurchaseMethodPair(
  a: PurchaseMethodPair | null,
  b: PurchaseMethodPair | null
): boolean {
  if (!a || !b) {
    return false;
  }

  return a.buyerCode === b.buyerCode && a.purchaseMethod === b.purchaseMethod;
}

/**
 * Single definition of "the purchase method blocks confirming or generating", shared by
 * every surface that can edit the field so they cannot drift apart.
 *
 * Blocks unless the value is set AND the integration approved it AND that approval
 * belongs to the combination currently on screen. A pending check is not proof, an
 * unavailable integration is not proof, and a verdict earned by a different buyer or a
 * different purchase method is not proof either.
 *
 * @param current - The buyer and purchase method the operator has on screen.
 * @param verdict - The stored verdict together with the combination it was obtained for.
 * @returns true when the action must stay disabled.
 */
export function isPurchaseMethodGateBlocked(
  current: PurchaseMethodPair,
  verdict: PurchaseMethodVerdict
): boolean {
  if (current.buyerCode <= 0 || !isValidPurchaseMethod(current.purchaseMethod)) {
    return true;
  }

  if (verdict.status !== PURCHASE_METHOD_RESPONSE.VALID) {
    return true;
  }

  return !isSamePurchaseMethodPair(current, verdict.pair);
}
